import { JSX, useMemo, useState } from "react";
import { useApp, Language } from "@/contexts/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Globe, Save, Edit } from "lucide-react";
import { toast } from "sonner";
import { translationService } from "@/services/translation.service";
import { de as deBaseTranslations } from "@/locales/de";
import { en as enBaseTranslations } from "@/locales/en";

const hasTopLevelEntries = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && Object.keys(value).length > 0;

export function LanguageManagementPanel() {
  const {
    t,
    language,
    availableLanguages,
    translations,
    updateTranslations,
    reloadTranslations,
  } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const chipStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 22%, var(--background))",
    color: "color-mix(in srgb, var(--foreground) 92%, var(--background))",
    border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--background))",
  } as const;
  const borderStyle = {
    borderColor: "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const [editingLang, setEditingLang] = useState<Language>("de");
  const [editingKey, setEditingKey] = useState("");
  const [editingValue, setEditingValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const beginEdit = (lang: Language, key: string, value: string) => {
    setEditingLang(lang);
    setEditingKey(key);
    setEditingValue(value);
  };

  const getLanguageDisplay = (
    lang: Language
  ): {
    name: string;
    code: string;
  } => {
    switch (lang) {
      case "de":
        return { name: "Deutsch", code: "DE" };
      case "en":
        return { name: "English", code: "EN" };
    }
  };

  const handleSaveTranslation = async () => {
    if (!editingKey || !editingValue) {
      toast.error(t("admin.fillAllFields"));
      return;
    }

    const currentTranslations = { ...translations[editingLang] };
    const keys = editingKey.split(".");

    let obj: any = currentTranslations;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) {
        obj[keys[i]] = {};
      }
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = editingValue;

    try {
      await translationService.updateOverride(
        editingLang,
        editingKey,
        editingValue
      );
      updateTranslations(editingLang, currentTranslations);
      translationService.invalidateOverrides(editingLang);
      translationService.notifyOverridesUpdated(editingLang);
      void reloadTranslations({ force: true, languages: [editingLang] });
      toast.success(
        language === "de" ? "Übersetzung gespeichert" : "Translation saved"
      );
      setEditingKey("");
      setEditingValue("");
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? t("common.genericError"));
    }
  };

  const renderTranslationObject = (
    obj: any,
    prefix = "",
    query = ""
  ): JSX.Element[] => {
    const elements: JSX.Element[] = [];
    const normalizedQuery = query.trim().toLowerCase();

    Object.keys(obj).forEach((key) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (typeof value === "object" && value !== null) {
        const childElements = renderTranslationObject(
          value,
          fullKey,
          normalizedQuery
        );
        if (childElements.length > 0) {
          elements.push(
            <div key={fullKey} className="mb-4">
              <h4 className="mb-2" style={primaryTextStyle}>
                {fullKey}
              </h4>
              <div className="pl-4 border-l-2" style={borderStyle}>
                {childElements}
              </div>
            </div>
          );
        }
      } else {
        if (
          !normalizedQuery ||
          fullKey.toLowerCase().includes(normalizedQuery)
        ) {
          elements.push(
            <div
              key={fullKey}
              className="flex items-start gap-2 mb-3 p-3 border rounded-lg"
              style={borderStyle}
            >
              <div className="flex-1">
                <p className="text-sm mb-1" style={secondaryTextStyle}>
                  {fullKey}
                </p>
                <p style={primaryTextStyle}>{value}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  beginEdit(editingLang, fullKey, String(value));
                }}
              >
                <Edit className="w-4 h-4" style={iconStyle} />
              </Button>
            </div>
          );
        }
      }
    });

    return elements;
  };

  const deSourceTranslations = useMemo<Record<string, unknown>>(
    () =>
      hasTopLevelEntries(translations.de)
        ? (translations.de as Record<string, unknown>)
        : (deBaseTranslations as Record<string, unknown>),
    [translations.de]
  );

  const enSourceTranslations = useMemo<Record<string, unknown>>(
    () =>
      hasTopLevelEntries(translations.en)
        ? (translations.en as Record<string, unknown>)
        : (enBaseTranslations as Record<string, unknown>),
    [translations.en]
  );

  const mobileEditingSource =
    editingLang === "de" ? deSourceTranslations : enSourceTranslations;

  const mobileFilteredTranslations = renderTranslationObject(
    mobileEditingSource,
    "",
    searchQuery
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-0">
      <div className="lg:col-span-1 space-y-6">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle
              className="card-title-text flex items-center gap-2"
              style={primaryTextStyle}
            >
              {t("admin.availableLanguages")}
            </CardTitle>
            <CardDescription
              className="card-description-text"
              style={secondaryTextStyle}
            >
              {t("admin.chooseLanguage")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {availableLanguages.map((lang) => {
              const display = getLanguageDisplay(lang);
              return (
                <Button
                  key={lang}
                  variant={editingLang === lang ? "default" : "outline"}
                  className="w-full justify-start"
                  onClick={() => setEditingLang(lang)}
                >
                  <span className="mr-2 px-2 py-0.5 rounded" style={chipStyle}>
                    {display.code}
                  </span>
                  {display.name}
                </Button>
              );
            })}
          </CardContent>
        </Card>

        {editingKey && (
          <Card className="stat-card">
            <CardHeader>
            <CardTitle className="card-title-text" style={primaryTextStyle}>
              {t("admin.editTranslation")}
            </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label style={primaryTextStyle}>{t("admin.key")}</Label>
                <Input value={editingKey} disabled className="input-standard" />
              </div>
              <div className="space-y-2">
                <Label style={primaryTextStyle}>{t("admin.value")}</Label>
                <Textarea
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  rows={3}
                  className="input-standard"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveTranslation}
                  className="btn-primary gap-2"
                >
                  <Save className="w-4 h-4" />
                  {t("common.save")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingKey("");
                    setEditingValue("");
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="lg:col-span-2">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="card-title-text" style={primaryTextStyle}>
              <span className="lg:hidden">
                {t("admin.translations")} -
                <span className="ml-2 px-2 py-0.5 rounded" style={chipStyle}>
                  {getLanguageDisplay(editingLang).code}
                </span>
                <span className="ml-2">{getLanguageDisplay(editingLang).name}</span>
              </span>
              <span className="hidden lg:inline">{t("admin.translations")}</span>
            </CardTitle>
            <CardDescription
              className="card-description-text"
              style={secondaryTextStyle}
            >
              {t("admin.clickToEdit")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 space-y-2">
              <Label style={primaryTextStyle}>
                {t("admin.searchTranslations")}
              </Label>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("admin.searchTranslationsPlaceholder")}
                className="input-standard"
              />
            </div>

            {/* Side-by-side matrix variant is paused; keep stable legacy editor rendering. */}
            <div className="max-h-[600px] overflow-y-auto space-y-2">
              {mobileFilteredTranslations}
              {mobileFilteredTranslations.length === 0 && (
                <div className="flex items-center gap-2 text-secondary">
                  <Globe className="w-4 h-4" style={iconStyle} />
                  <span>{t("common.noResults")}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
